import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processSessionId, checkAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Signing you in...');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (sessionIdMatch) {
          const sessionId = sessionIdMatch[1];
          setMessage('Verifying your credentials...');
          
          const result = await processSessionId(sessionId);
          
          if (result.success && result.user) {
            setStatus('success');
            setMessage(`Welcome back, ${result.user.name || 'User'}!`);
            
            // Get the intended redirect path from localStorage or default to home.
            // Defensive: ignore non-string-path values (e.g. stale "[object Object]" from a prior bug).
            const stored = localStorage.getItem('auth_redirect');
            const intendedPath = (typeof stored === 'string' && stored.startsWith('/') && !stored.includes('[object')) ? stored : '/';
            localStorage.removeItem('auth_redirect');
            
            // Small delay to show success message
            setTimeout(() => {
              // Clear the hash from URL and navigate
              window.history.replaceState(null, '', window.location.pathname);
              navigate(intendedPath, { replace: true });
            }, 800);
          } else {
            setStatus('error');
            setMessage(result?.message || 'Authentication failed. Please try again.');
            setTimeout(() => {
              navigate('/', { replace: true });
            }, 2600);
          }
        } else {
          // No session_id found - check if already authenticated
          setMessage('Checking authentication status...');
          await checkAuth();
          
          const stored = localStorage.getItem('auth_redirect');
          const intendedPath = (typeof stored === 'string' && stored.startsWith('/') && !stored.includes('[object')) ? stored : '/';
          localStorage.removeItem('auth_redirect');
          navigate(intendedPath, { replace: true });
        }
      } catch (error) {
        console.error('Auth processing error:', error);
        setStatus('error');
        setMessage('An error occurred. Redirecting...');
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 via-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center bg-white/80 backdrop-blur-xl rounded-2xl p-8 shadow-xl border border-gray-200 max-w-sm mx-4">
        {status === 'processing' && (
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
        )}
        {status === 'success' && (
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        )}
        {status === 'error' && (
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        )}
        <p className={`font-medium ${
          status === 'success' ? 'text-green-600' : 
          status === 'error' ? 'text-red-600' : 
          'text-gray-600'
        }`}>
          {message}
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
